import React, { useState } from 'react';
import {
  Menu,
  X,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Cpu,
  Workflow,
  Cloud,
  Mail,
  ExternalLink,
} from 'lucide-react';

const CONTACT_EMAIL = 'contact@wgxtechnologies.com';

const services = [
  {
    icon: Cpu,
    title: 'AI Product Engineering',
    summary:
      'Design and launch practical AI features that fit existing team workflows, not demo-only prototypes.',
    points: ['Discovery workshops', 'Model and API integration', 'Production release support'],
  },
  {
    icon: Workflow,
    title: 'Workflow Automation',
    summary:
      'Remove repetitive manual work with automation pipelines that connect your tools, data, and people.',
    points: ['Business process mapping', 'No-code + custom automation', 'Monitoring and optimization'],
  },
  {
    icon: ShieldCheck,
    title: 'Secure Cloud Delivery',
    summary:
      'Ship with confidence through scalable infrastructure, security-first architecture, and measurable reliability.',
    points: ['Cloud-native architecture', 'Security hardening', 'Performance and uptime tuning'],
  },
];

const deliverySteps = [
  {
    title: '1. Clarify the business goal',
    description:
      'We define success metrics, user flows, and technical constraints before any build work starts.',
  },
  {
    title: '2. Prototype the highest-value flow',
    description:
      'A focused proof of value lets your team validate outcomes quickly with minimal disruption.',
  },
  {
    title: '3. Build production-grade systems',
    description:
      'We implement the complete solution with scalable architecture, observability, and documentation.',
  },
  {
    title: '4. Iterate on real usage data',
    description:
      'After launch, we tune reliability and user experience based on feedback and performance signals.',
  },
];

const privacySections = [
  {
    title: 'Information We Collect',
    paragraphs: [
      'We may collect information you provide directly, such as your name, email address, and message details when you contact us.',
      'We may also collect technical information automatically, including browser type, device details, pages visited, and approximate location derived from IP address.',
    ],
  },
  {
    title: 'How We Use Information',
    paragraphs: [
      'We use collected information to operate and improve our website, respond to inquiries, protect site security, and support our business operations.',
      'We may use aggregated, non-identifiable analytics data to understand website performance and user experience trends.',
    ],
  },
  {
    title: 'Advertising and Cookies',
    paragraphs: [
      'This site may use advertising technologies, including Google AdSense, which can use cookies or similar technologies to personalize or measure ads.',
      'You can manage cookie preferences through your browser settings. Disabling cookies may affect some site functionality.',
    ],
  },
  {
    title: 'Data Sharing',
    paragraphs: [
      'We do not sell your personal information. We may share limited data with service providers that support hosting, analytics, advertising, or security under appropriate safeguards.',
      'We may disclose information when required by law, legal process, or to protect our rights and users.',
    ],
  },
  {
    title: 'Data Retention and Security',
    paragraphs: [
      'We retain information only as long as needed for legitimate business and legal purposes.',
      'We use reasonable administrative and technical safeguards to protect personal information, but no internet transmission method is completely secure.',
    ],
  },
  {
    title: 'Your Rights and Choices',
    paragraphs: [
      'Depending on your location, you may have rights to access, correct, delete, or limit use of personal information. To make a request, contact us using the email below.',
      'You can opt out of marketing communications at any time by following unsubscribe instructions or contacting us directly.',
    ],
  },
  {
    title: 'Contact',
    paragraphs: [
      'If you have questions about this Privacy Policy or your data, contact us at contact@wgxtechnologies.com.',
    ],
  },
];

function HomePage({ onOpenPrivacy, onToggleContact }) {
  return (
    <main className="main-content">
      <section className="hero section-spacing">
        <div className="hero-grid">
          <div>
            <p className="eyebrow">AI Product and Automation Studio</p>
            <h1>Build AI software that delivers measurable business outcomes.</h1>
            <p className="hero-copy">
              WGX Technologies partners with teams that want to move from manual operations to
              reliable, modern software systems. We design, build, and improve products your team
              can trust in production.
            </p>
            <div className="hero-actions">
              <button className="btn btn-primary" onClick={onToggleContact}>
                Start a project <ArrowRight size={18} />
              </button>
              <button className="btn btn-secondary" onClick={onOpenPrivacy}>
                Privacy Policy
              </button>
            </div>
            <div className="trust-strip" role="list">
              <span className="trust-item" role="listitem">
                <CheckCircle2 size={16} /> Product-first delivery
              </span>
              <span className="trust-item" role="listitem">
                <CheckCircle2 size={16} /> Security-minded implementation
              </span>
              <span className="trust-item" role="listitem">
                <CheckCircle2 size={16} /> Clear project communication
              </span>
            </div>
          </div>

          <aside className="hero-panel" aria-label="Delivery highlights">
            <h2>Recent delivery focus</h2>
            <div className="hero-panel-list">
              <div className="hero-panel-item">
                <Cpu size={20} />
                <div>
                  <h3>AI-enabled software</h3>
                  <p>Practical model integrations that support real user workflows.</p>
                </div>
              </div>
              <div className="hero-panel-item">
                <Workflow size={20} />
                <div>
                  <h3>Operational automation</h3>
                  <p>Process improvements that reduce repeat work and human error.</p>
                </div>
              </div>
              <div className="hero-panel-item">
                <Cloud size={20} />
                <div>
                  <h3>Scalable deployment</h3>
                  <p>Cloud infrastructure patterns built for reliability and growth.</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="section-spacing section-light">
        <div className="section-header">
          <p className="eyebrow">Services</p>
          <h2>What WGX Technologies can help you ship</h2>
          <p>
            Engagements are structured around business priorities, delivery speed, and long-term
            maintainability.
          </p>
        </div>

        <div className="services-grid">
          {services.map(({ icon: Icon, title, summary, points }) => (
            <article className="service-card" key={title}>
              <div className="service-icon">
                <Icon size={22} />
              </div>
              <h3>{title}</h3>
              <p>{summary}</p>
              <ul className="bullet-list">
                {points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="section-spacing">
        <div className="section-header">
          <p className="eyebrow">Process</p>
          <h2>A predictable delivery flow from kickoff to launch</h2>
        </div>

        <div className="process-grid">
          {deliverySteps.map((step) => (
            <article className="process-card" key={step.title}>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section-spacing section-light">
        <div className="spotlight-card">
          <div>
            <p className="eyebrow">Product Spotlight</p>
            <h2>My Vet Assistant</h2>
            <p>
              A focused veterinary workflow platform that combines appointment operations,
              communication, and AI-assisted record workflows in one product experience.
            </p>
            <a
              className="inline-link"
              href="https://myvetassistant.com"
              target="_blank"
              rel="noreferrer"
            >
              Visit myvetassistant.com <ExternalLink size={16} />
            </a>
          </div>
          <ul className="spotlight-list">
            <li>Appointment and practice workflow support</li>
            <li>AI-assisted records and operational notes</li>
            <li>Improved communication flow for staff and clients</li>
          </ul>
        </div>
      </section>

      <section className="section-spacing cta-section">
        <div className="cta-card">
          <h2>Ready to modernize your product and operations?</h2>
          <p>
            Share your current workflow and goals. We can map out a practical rollout plan for your
            team.
          </p>
          <button className="btn btn-primary" onClick={onToggleContact}>
            Contact WGX Technologies <Mail size={18} />
          </button>
        </div>
      </section>
    </main>
  );
}

function PrivacyPolicyPage({ onOpenHome, onToggleContact }) {
  return (
    <main className="main-content privacy-page">
      <section className="privacy-hero section-spacing">
        <p className="eyebrow">Privacy Policy</p>
        <h1>Privacy practices for wgxtechnologies.com</h1>
        <p>
          Last updated: April 8, 2026. This policy explains what information we collect, how we
          use it, and the choices available to visitors.
        </p>
        <div className="hero-actions">
          <button className="btn btn-secondary" onClick={onOpenHome}>
            Back to home
          </button>
          <button className="btn btn-primary" onClick={onToggleContact}>
            Contact us
          </button>
        </div>
      </section>

      <section className="section-spacing privacy-content">
        {privacySections.map((section) => (
          <article className="privacy-card" key={section.title}>
            <h2>{section.title}</h2>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </article>
        ))}
      </section>
    </main>
  );
}

function Footer({ onOpenHome, onOpenPrivacy, onToggleContact, showContact }) {
  return (
    <footer className="site-footer">
      <div className="footer-grid">
        <div>
          <div className="brand">
            <span className="brand-mark">W</span>
            <span>WGX Technologies</span>
          </div>
          <p>
            We build secure AI-powered software and automation systems that help teams operate with
            more speed and confidence.
          </p>
        </div>

        <div>
          <h3>Navigate</h3>
          <div className="footer-links">
            <button className="footer-link" onClick={onOpenHome}>
              Home
            </button>
            <button className="footer-link" onClick={onOpenPrivacy}>
              Privacy Policy
            </button>
            <button className="footer-link" onClick={onToggleContact}>
              Contact
            </button>
          </div>
        </div>

        <div>
          <h3>Contact</h3>
          <p>Click contact to reveal our support email.</p>
          {showContact && (
            <a className="footer-email" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>
          )}
        </div>
      </div>
      <p className="footer-note">&copy; {new Date().getFullYear()} WGX Technologies. All rights reserved.</p>
    </footer>
  );
}

function LandingPage() {
  const [currentPage, setCurrentPage] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showContact, setShowContact] = useState(false);

  const openHome = () => {
    setCurrentPage('home');
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openPrivacy = () => {
    setCurrentPage('privacy');
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleContact = () => {
    setShowContact((previous) => !previous);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="site-shell">
      <header className="site-header">
        <div className="header-inner">
          <button className="brand-button" onClick={openHome}>
            <span className="brand-mark">W</span>
            <span className="brand">WGX Technologies</span>
          </button>

          <nav className="desktop-nav" aria-label="Primary navigation">
            <button className={`nav-btn ${currentPage === 'home' ? 'is-active' : ''}`} onClick={openHome}>
              Home
            </button>
            <button
              className={`nav-btn ${currentPage === 'privacy' ? 'is-active' : ''}`}
              onClick={openPrivacy}
            >
              Privacy Policy
            </button>
            <button className="btn btn-primary nav-contact" onClick={toggleContact}>
              Contact
            </button>
          </nav>

          <button
            className="mobile-toggle"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label="Toggle navigation menu"
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <nav className="mobile-nav" aria-label="Mobile navigation">
            <button className="mobile-nav-btn" onClick={openHome}>
              Home
            </button>
            <button className="mobile-nav-btn" onClick={openPrivacy}>
              Privacy Policy
            </button>
            <button className="mobile-nav-btn" onClick={toggleContact}>
              Contact
            </button>
          </nav>
        )}

        {showContact && (
          <div className="contact-banner" role="status" aria-live="polite">
            <Mail size={16} />
            <span>Contact email:</span>
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </div>
        )}
      </header>

      {currentPage === 'home' ? (
        <HomePage onOpenPrivacy={openPrivacy} onToggleContact={toggleContact} />
      ) : (
        <PrivacyPolicyPage onOpenHome={openHome} onToggleContact={toggleContact} />
      )}

      <Footer
        onOpenHome={openHome}
        onOpenPrivacy={openPrivacy}
        onToggleContact={toggleContact}
        showContact={showContact}
      />
    </div>
  );
}

export default LandingPage;
